/**
 * Media playback failures are not application errors.
 *
 * `HTMLMediaElement.play()` returns a promise that REJECTS with
 * `NotSupportedError: The element has no supported sources.` whenever the
 * element's resource selection found nothing playable — an expired/404 artifact
 * URL, a revoked `blob:` URL, a codec the browser will not decode, or a `src`
 * that was never set. Chromium's rejection carries no useful stack, so it lands
 * in Sentry as an untraceable mystery (issue JAVASCRIPT-4, one user, one event,
 * on the staging agents page).
 *
 * Nothing in this app calls `.play()` itself: every `<audio>`/`<video>` we render
 * is driven by the browser's own controls, or by whatever script is sharing the
 * page (extensions, password managers, "read aloud" tooling). So the only way
 * such a rejection can appear is from outside our call graph, and it says
 * nothing we can act on. The elements themselves now handle their `error` event
 * and show the user a download/unavailable fallback instead (see
 * ArtifactViewerPanel and VaultPreviewDialog), which is the handled, quiet
 * failure path — so this predicate keeps the duplicate unhandled rejection out
 * of Sentry rather than letting it crowd out real crashes.
 *
 * Pure, so it is unit-testable — see mediaErrors.test.ts.
 */

/**
 * True when `reason` is a media element reporting that it has no playable
 * source. Matched by DOMException name plus a media-shaped message, because the
 * wording differs per engine:
 *  - Chromium: "The element has no supported sources."
 *  - Firefox:  "The media resource indicated by the src attribute … was not suitable."
 *  - WebKit:   "The operation is not supported." (with a media source reference)
 */
export function isUnsupportedMediaSourceError(reason: unknown): boolean {
  if (!reason || typeof reason !== "object") return false;
  const candidate = reason as { readonly name?: unknown; readonly message?: unknown };
  if (candidate.name !== "NotSupportedError") return false;
  if (typeof candidate.message !== "string") return false;
  const message = candidate.message.toLowerCase();
  return (
    message.includes("supported source") ||
    message.includes("media resource") ||
    message.includes("media provider")
  );
}
